import { apiRequest, attemptSessionRestore, refreshAccessToken, tokenManager } from "./client";
import axios from "axios";
import type { components as BackendComponents } from "./generated/backend";

import { resolveApiBaseUrl } from "@/lib/api/base-url";
import type { CompanyRole } from "@/lib/companyRole";
import type { FeatureFlags } from "@/lib/features";
import { logger } from "@/lib/logger";

// Types based on backend API schema
export interface LoginCredentials {
  username: string; // Backend uses username field (can be email or username)
  password: string;
  grant_type?: string;
  scope?: string;
  client_id?: string;
  client_secret?: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: UserMeResponse;
}

/**
 * Body shape returned by `POST /auth/refresh` (cookie-based refresh flow,
 * consultant-portal-backend#490). The user object is *not* returned on
 * refresh — only the new access token. The frontend re-fetches the user via
 * `GET /users/me` when it needs it; nothing user-related is persisted in web
 * storage (issue #635).
 */
export interface RefreshResponse {
  access_token: string;
  token_type?: string;
}

export interface UserMeResponse {
  id: number;
  username: string;
  email: string;
  role: string;
  is_active: boolean;
  created_at: string;
  full_name?: string | null;
  consultant_profile?: ConsultantProfile | null;
  /** The consultant's role in their own company (BE #1069): a manager signs
   *  and sees the company's terms and invoices, an employee time-reports.
   *  Null for admins; missing on older backends, which reads as manager. */
  company_role?: CompanyRole | null;
  /** A published policy still needs acknowledging (BE #864); never set for admins. */
  has_pending_policies?: boolean;
  /** Environment feature flags (BE #898); missing means everything optional is off. */
  features?: FeatureFlags | null;
}

export interface ConsultantProfile {
  id: number;
  full_name: string;
  email?: string | null;
  linkedin_url?: string | null;
  phone?: string | null;
}

export interface UserProfileResponse {
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  mobile?: string | null;
  contact_email?: string | null;
  street?: string | null;
  city?: string | null;
  postal_code?: string | null;
  country?: string | null;
  position?: string | null;
  department?: string | null;
  bio?: string | null;
  linkedin_url?: string | null;
  github_url?: string | null;
  website_url?: string | null;
  available_from?: string | null;
  id: number;
  user_id: number;
  company_id?: number | null;
  company?: { id: number; name: string; org_number?: string } | null;
  avatar_initials: string;
  created_at: string;
  updated_at: string;
}

export interface UserProfileUpdate {
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  mobile?: string | null;
  contact_email?: string | null;
  street?: string | null;
  city?: string | null;
  postal_code?: string | null;
  country?: string | null;
  position?: string | null;
  department?: string | null;
  bio?: string | null;
  linkedin_url?: string | null;
  github_url?: string | null;
  website_url?: string | null;
  available_from?: string | null;
  org_number?: string | null;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  new_password: string;
}

export interface UserSettingsResponse {
  preferences?: Record<string, unknown> | null;
  notifications?: Record<string, unknown> | null;
  privacy?: Record<string, unknown> | null;
  dashboard?: Record<string, unknown> | null;
  id: number;
  user_id: number;
  created_at: string;
  updated_at: string;
}

export interface UserSettingsUpdate {
  preferences?: Record<string, unknown> | null;
  notifications?: Record<string, unknown> | null;
  privacy?: Record<string, unknown> | null;
  dashboard?: Record<string, unknown> | null;
}

export type UserPermissionsResponse = BackendComponents["schemas"]["UserPermissionsResponse"];

// Authentication API Service
export const authAPI = {
  /**
   * Login with username/email and password
   * Backend expects form-encoded data for OAuth2 password flow
   */
  login: async (credentials: { username: string; password: string }): Promise<LoginResponse> => {
    try {
      // Create form data for OAuth2 password flow
      const formData = new FormData();
      formData.append("username", credentials.username);
      formData.append("password", credentials.password);
      formData.append("grant_type", "password");

      // Use axios directly for form data (different content-type).
      // `withCredentials: true` is required so the browser accepts and
      // stores the HttpOnly refresh-token cookie set by the backend
      // (issue #509 / consultant-portal-backend#490).
      const response = await axios.post("/auth/login", formData, {
        baseURL: resolveApiBaseUrl(),
        withCredentials: true,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      const loginData: LoginResponse = response.data;

      // Keep the access token in memory only (issue #635) — the refresh
      // token arrives as an HttpOnly cookie alongside this response.
      tokenManager.setToken(loginData.access_token);

      return loginData;
    } catch (error) {
      logger.error("login_failed", { err: String(error) });
      throw error;
    }
  },

  /**
   * Get current user information
   */
  getCurrentUser: async (): Promise<UserMeResponse> => {
    return apiRequest.get<UserMeResponse>("/users/me");
  },

  /**
   * Get the signed-in user's effective permissions. UI capability checks must
   * use this exact set rather than infer access from a role name.
   */
  getCurrentUserPermissions: async (): Promise<UserPermissionsResponse> => {
    return apiRequest.get<UserPermissionsResponse>("/auth/permissions", {
      suppressErrorToast: true,
    });
  },

  /**
   * Get user profile information
   */
  getUserProfile: async (): Promise<UserProfileResponse> => {
    return apiRequest.get<UserProfileResponse>("/users/profile");
  },

  /**
   * Update user profile
   */
  updateUserProfile: async (profile: UserProfileUpdate): Promise<UserProfileResponse> => {
    return apiRequest.put<UserProfileResponse>("/users/profile", profile);
  },

  /**
   * Change user password
   */
  changePassword: async (passwordData: ChangePasswordRequest): Promise<void> => {
    return apiRequest.post("/users/change-password", passwordData);
  },

  /**
   * Request a password reset email
   */
  forgotPassword: async (data: ForgotPasswordRequest): Promise<void> => {
    return apiRequest.post("/auth/forgot-password", data);
  },

  /**
   * Reset password using token from email
   */
  resetPassword: async (data: ResetPasswordRequest): Promise<void> => {
    return apiRequest.post("/auth/reset-password", data);
  },

  /**
   * Get user settings
   */
  getUserSettings: async (): Promise<UserSettingsResponse> => {
    return apiRequest.get<UserSettingsResponse>("/users/settings");
  },

  /**
   * Update user settings
   */
  updateUserSettings: async (settings: UserSettingsUpdate): Promise<UserSettingsResponse> => {
    return apiRequest.put<UserSettingsResponse>("/users/settings", settings);
  },

  /**
   * Get user sessions (placeholder)
   */
  getUserSessions: async (): Promise<unknown> => {
    return apiRequest.get("/users/sessions");
  },

  /**
   * Logout — invalidate refresh-token cookie server-side, then clear local
   * storage. The backend endpoint (consultant-portal-backend#490) returns a
   * `Set-Cookie` clearing `bondit_refresh_token` and revokes the refresh
   * JWT server-side. Errors are non-fatal: local storage is always cleared
   * so the user is logged out client-side regardless of the network result.
   */
  logout: async (): Promise<void> => {
    try {
      await apiRequest.post("/auth/logout", null, { suppressErrorToast: true });
    } catch (error) {
      logger.warn("logout_api_failed", { err: String(error) });
    } finally {
      tokenManager.removeToken();
    }
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: (): boolean => {
    const token = tokenManager.getToken();
    return !!token;
  },

  /**
   * Exchange the refresh-token cookie for a fresh access token (silent
   * refresh). Delegates to the client's shared single-flight refresh so a
   * timer-driven refresh can never race a 401-driven one (or another tab's)
   * and double-consume the rotating refresh cookie.
   */
  refreshToken: async (): Promise<RefreshResponse> => {
    const access_token = await refreshAccessToken();
    return { access_token };
  },

  /**
   * Restore a session on app bootstrap by exchanging the HttpOnly
   * refresh-token cookie for a fresh in-memory access token (issue #635).
   * Resolves with the token, or `null` when the user has no valid session.
   */
  restoreSession: async (): Promise<string | null> => {
    return attemptSessionRestore();
  },

  /**
   * Refresh user data from API
   */
  refreshUserData: async (): Promise<UserMeResponse | null> => {
    try {
      if (!authAPI.isAuthenticated()) {
        return null;
      }

      return await authAPI.getCurrentUser();
    } catch (error) {
      logger.error("refresh_user_failed", { err: String(error) });
      // If refresh fails due to invalid token, logout
      if (error instanceof Error && error.message.includes("401")) {
        tokenManager.removeToken();
      }
      return null;
    }
  },
};

export default authAPI;
